// ============================================================
// PlateGoo - the amorphous mass (plan body-map P7, S2; the goo study): soft drops fused into one surface by a
// filter (a blur, then a hard alpha threshold) and only its edge kept - the rim, and two inner walls lifted as
// contour lines over a dome; scanlines through the volume, faint within and bright at the rim; the projector's ring
// on the ground under it. The diagram over it is a tracked callout (MASS and its armour) and dimension lines that
// read the sheet (Size tall, marker diameter wide). It moves by its own body (gooLive) on the plate's clock - its
// nature, not a condition - and its conditions change that body and take layers over it (fx/gooConditions). With
// motion off it holds the settled shape its conditions give at rest. Its hits come with Track H.
//
// It keeps the old map's names: `amorphous body hit location map`, a `<path style="cursor: context-menu">` for the
// armour edit on the mass.
// ============================================================

import { useMemo, useRef, useState, type CSSProperties } from 'react';
import clsx from '../../utils/clsx';
import { plateBand } from '../../engine/plate/plateBand';
import { gooAtRest, gooBounds, gooFrame, gooMetres, type Drop } from '../../engine/plate/goo';
import { FOOT, gooBody, gooPace } from '../../engine/plate/gooLive';
import { gooHitsOf, gooSpan, gooSparks, massDrops, settledDrops, struckGoo } from '../../engine/plate/gooHits';
import type { Impact } from '../../engine/impacts';
import usePlateHits from './usePlateHits';
import useHoldsStill from '../../hooks/useHoldsStill';
import { BurstMarks } from './fx/burst';
import { StrikeMoments } from './fx/moments';
import type { Box as FrameBox, PlateFrame } from '../../engine/plate/frame';
import { firstLanded, type Burst } from '../../engine/plate/burst';
import { guardOf } from '../../engine/plate/moments';
import fx from '../../styles/PlateFx.module.css';
import { approach } from '../../engine/display/timeline';
import { type PlateState } from '../../engine/plate/plateState';
import { isLocCoveredForBody, getBodyConfig } from '../../engine/bodyConfig';
import type { Target } from '../../types';
import usePlateState from './usePlateState';
import styles from '../../styles/BodyPlate.module.css';
import {
  CoverPlus,
  StatusLine,
  statusWords,
  OpenSight,
  PlateHit,
  plateSize,
  usePlateUid,
  viewBoxOf,
} from './plateParts';
import usePlateClock, { tracked } from './usePlateClock';
import type { HitPlay } from './usePlateHits';
import {
  boxDraw,
  looksMove,
  TraitBack,
  TraitDefs,
  traitFrame,
  TraitFront,
  TraitOver,
  TraitUnder,
  type TraitDraw,
  type TraitFrame,
} from './fx/traits/TraitLayers';
import { traitLooks } from '../../engine/plate/traitLooks';
import type { Character } from '../../engine/character/types';
import {
  dropNodes,
  GooConditionDefs,
  gooColourClass,
  gooConditionOf,
  GooLayers,
  gooScene,
  sparkNodes,
} from './fx/gooConditions';
import { applyScene, SceneView, useSceneWriter, writeScene } from './fx/scene';

/** The study's whole frame: what the traits drawn round the mass may fill (the plate itself fits `gooFrame`). */
const VB = { x: -200, y: 0, w: 1000, h: 820 };
const GROUND_Y = FOOT;
/** The rim and the two inner walls: how far each is pulled toward the core and shrunk, its line width and strength. */
const WALLS = [
  { pull: 0, size: 1, width: 3.5, opacity: 1 },
  { pull: 0.45, size: 0.66, width: 1.6, opacity: 0.7 },
  { pull: 0.7, size: 0.38, width: 1.2, opacity: 0.45 },
];
const AMORPHOUS = getBodyConfig('amorphous');
const FLOOD = { floodColor: 'var(--goo, var(--line))' } as CSSProperties;
/** The light on the mass's volume: the study's white-hot crescent where it faces the light (top-left). */
const LIT = { floodColor: 'var(--plate-goo-lit)' } as CSSProperties;
/** The scanlines drift up through the mass, 24 units every 3 s (the study's); held at rest with motion off. */
const SCAN_DRIFT = tracked([{ attr: 'dy', values: [0, -24], dur: 3 }]);

interface Props {
  target: Target;
  /** The combatant's own sheet: the traits it wears (plan design-gaps, P8). */
  sheet?: Character | null;
  side?: 'target' | 'actor';
  fill?: boolean;
  onLocationContextMenu?: (id: string) => void;
  /** What just happened to it (engine/impacts): a new one plays once (plan body-map H1). */
  impact?: Impact | null;
}

/** The mass's one location, as the impacts name it. */
const MASS = ['mass'];
/** The struck mass moves longer than the burst takes: its play is held for it. */
const holdMass = (burst: Burst) => gooSpan(burst.hits);

export default function PlateGoo(props: Props) {
  const { target, sheet = null, side = 'target', fill = false, onLocationContextMenu, impact } = props;
  const u = usePlateUid('goo');
  const { cores } = GOO_AT_REST;
  const box = GOO_BOX;
  const { state, dataStates, glow } = usePlateState(target.conditions);
  // Fitted to what it draws (plan plate-unboxed), the status line's room only while it says something.
  const fit = gooFrame(box, statusWords(state) !== '');
  const still = useMemo(() => stillScene(state), [state]);
  const ref = useRef<SVGSVGElement>(null);
  const hits = usePlateHits(impact, MASS, holdMass);
  const moving = !useHoldsStill();
  const looks = useGooLooks(target, sheet, state.dead, u, hits);
  const live = useGooLive(state, still, hits, looks.frame);
  usePlateClock(ref, live.moving || hits.play !== null || looksMove(looks.d), live);
  return (
    <svg
      ref={ref}
      viewBox={viewBoxOf(fit)}
      className={clsx(
        plateSize(fill),
        styles.plate,
        side === 'actor' && styles.actor,
        state.dead && styles.dead,
        gooColourClass(state),
      )}
      data-band={plateBand(target, state.dead)}
      style={glow}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="amorphous body hit location map"
      data-figure="goo"
      data-states={dataStates}
      data-hits={hits.play?.burst.hits.map((h) => h.location).join(' ') || undefined}
    >
      <GooTraitsBehind u={u} d={looks.d} play={hits.play} />
      <ProjectorBase cx={box.mx} reach={Math.min(260, (box.x1 - box.x0) / 2)} coreR={cores[0][2]} />
      <GooConditionDefs u={u} s={state} />
      <GooLayers u={u} s={state}>
        {/* Keyed by the play: each new hit restarts the shake. */}
        <StruckMass key={hits.play?.id} u={u} play={hits.play} still={still} />
        {hits.play && <StrikeMoments u={u} play={hits.play} frame={GOO_FRAME} guard={guardOf(target)} />}
      </GooLayers>
      <GooTraitsBefore u={u} d={looks.d} play={hits.play} />
      {hits.play && <BurstMarks u={u} play={hits.play} frame={GOO_FRAME} moving={moving} />}
      {state.helpless && <OpenSight box={[box.x0, box.y0, box.x1, box.y1]} margin={14} arm={30} />}
      <PlateHit
        id="mass"
        d={`M${box.x0} ${box.y0} H${box.x1} V${box.y1} H${box.x0} Z`}
        onContextMenu={onLocationContextMenu}
      />
      <MassCallout target={target} at={[box.mx, box.my]} />
      <Dimensions box={box} metres={gooMetres(target.size, target.tokenDiameter)} />
      <GooWounds state={state} left={fit.x + 40} />
    </svg>
  );
}

/** The mass as a hit finds it: its drops and walls, shaken by the first hit that landed, an Energy hit's sparks. */
function StruckMass({ u, play, still }: { u: string; play: HitPlay | null; still: ReturnType<typeof stillScene> }) {
  const landed = play && firstLanded(play.burst);
  return (
    <g className={clsx(landed && fx.gooStruck)} data-hit={landed?.type}>
      <GooMass u={u} drops={still.drops} />
      <g className={styles.gooMass}>
        <SceneView nodes={still.scene} />
        {play?.burst.hits.some((h) => h.type === 'E') && <SceneView nodes={sparkNodes(gooSparks([], GOO_CORE))} />}
      </g>
    </g>
  );
}

/** The mass at rest, in the study's frame: the fused rim and inner walls, and the volume's scanlines. */
const GOO_AT_REST = gooAtRest();
export const GOO_DROPS = settledDrops(GOO_AT_REST);
/** Where the steam's unseen wisps wait: the settled core. */
const GOO_CORE = { cx: GOO_AT_REST.cores[0][0], cy: GOO_AT_REST.cores[0][1], cr: GOO_AT_REST.cores[0][2] };
/** The settled mass's box in the study's frame - what a thumbnail or a crowd member frames. */
export const GOO_BOX = gooBounds(GOO_DROPS);
/**
 * Where the mass's hits land: round its settled box (its one part's box too), at the study's smaller scale, with no
 * lines of its own to glow.
 */
const GOO_BOUNDS = [GOO_BOX.x0, GOO_BOX.y0, GOO_BOX.x1, GOO_BOX.y1] as const;
const GOO_FRAME: PlateFrame = {
  box: GOO_BOUNDS,
  anchors: { mass: [GOO_BOX.mx, GOO_BOX.my] },
  partBox: () => GOO_BOUNDS,
  k: 0.3,
  lines: false,
};

interface MassProps {
  /** Keeps each drawing's filter ids its own. */
  u: string;
  /** The volume's scanlines (the plate's alone). */
  volume?: boolean;
  /** The inner walls - off for a crowd's members, twenty at a time, where each wall's filter costs a blur. */
  innerWalls?: boolean;
  /** Where its drops are drawn (the clock moves them on the plate); settled at rest by default. */
  drops?: readonly Drop[];
}

/** The settled goo itself (its filters and its drops), for the plate, the thumbnail and a crowd's members. */
export function GooMass({ u, volume = true, innerWalls = true, drops = GOO_DROPS }: MassProps) {
  const walls = innerWalls ? WALLS : WALLS.slice(0, 1);
  return (
    <>
      <GooFilters u={u} walls={walls} volume={volume} />
      <g className={styles.gooMass}>
        {volume && (
          <g filter={`url(#${u}-vol)`}>
            <SceneView nodes={dropNodes('vol', drops, 0, 1)} />
          </g>
        )}
        {walls.map((w, k) => (
          <g key={k} filter={`url(#${u}-wall${k})`}>
            <SceneView nodes={dropNodes(`w${k}`, drops, w.pull, w.size)} />
          </g>
        ))}
      </g>
    </>
  );
}

/** The fused surface's edge per wall (the inner ones lit), and the volume's light - the study's filters. */
function GooFilters({ u, walls, volume }: { u: string; walls: typeof WALLS; volume: boolean }) {
  return (
    <defs>
      {walls.map((w, k) => (
        <filter
          key={k}
          id={`${u}-wall${k}`}
          x="-15%"
          y="-15%"
          width="130%"
          height="130%"
          colorInterpolationFilters="sRGB"
        >
          <feGaussianBlur in="SourceGraphic" stdDeviation="14" result="b" />
          <feColorMatrix in="b" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 28 -11" result="goo" />
          <feMorphology in="goo" operator="erode" radius={w.width} result="e" />
          <feComposite in="goo" in2="e" operator="out" result="edge" />
          <feFlood style={FLOOD} floodOpacity={w.opacity} result="c" />
          <feComposite in="c" in2="edge" operator="in" result="p" />
          {k > 0 && <WallLight opacity={w.opacity} />}
        </filter>
      ))}
      {volume && <VolumeFilter u={u} />}
    </defs>
  );
}

/**
 * An inner wall lit like the rim (the study's depth, plan body-map ruling 2026-10-08): the wall less itself shifted
 * away from the light is its crescent facing it, softened and laid over the wall's edge.
 */
function WallLight({ opacity }: { opacity: number }) {
  return (
    <>
      <feOffset in="goo" dx="6" dy="8" result="sh" />
      <feComposite in="goo" in2="sh" operator="out" result="lit" />
      <feGaussianBlur in="lit" stdDeviation="2" result="litS" />
      <feFlood style={LIT} floodOpacity={opacity * 0.55} result="h" />
      <feComposite in="h" in2="litS" operator="in" result="hp" />
      <feMerge>
        <feMergeNode in="p" />
        <feMergeNode in="hp" />
      </feMerge>
    </>
  );
}

/**
 * The volume's light (the study's depth): a lit crescent where the mass faces the light, and scanlines through it,
 * faint within and bright at its rim, drifting up.
 */
function VolumeFilter({ u }: { u: string }) {
  return (
    <filter id={`${u}-vol`} x="-15%" y="-15%" width="130%" height="130%" colorInterpolationFilters="sRGB">
      <feGaussianBlur in="SourceGraphic" stdDeviation="14" result="b" />
      <feColorMatrix in="b" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 28 -11" result="goo" />
      <feOffset in="goo" dx="10" dy="13" result="sh" />
      <feComposite in="goo" in2="sh" operator="out" result="lit" />
      <feGaussianBlur in="lit" stdDeviation="3" result="litS" />
      <feFlood style={LIT} floodOpacity={0.5} result="hot" />
      <feComposite in="hot" in2="litS" operator="in" result="litP" />
      <feMorphology in="goo" operator="erode" radius="16" result="in0" />
      <feGaussianBlur in="in0" stdDeviation="12" result="inner" />
      <feComposite in="goo" in2="inner" operator="out" result="rim" />
      <feTurbulence type="turbulence" baseFrequency="0 0.085" numOctaves={1} seed={3} result="n" />
      <feColorMatrix in="n" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  1 0 0 0 0" result="na" />
      <feComponentTransfer in="na" result="stripes0">
        <feFuncA type="discrete" tableValues="0 0 0 1 0 0 0" />
      </feComponentTransfer>
      <feOffset in="stripes0" dx="0" result="stripes" {...SCAN_DRIFT} />
      <feComposite in="stripes" in2="goo" operator="in" result="sAll" />
      <feComposite in="stripes" in2="rim" operator="in" result="sRim" />
      <feFlood style={FLOOD} floodOpacity={0.16} result="c1" />
      <feComposite in="c1" in2="sAll" operator="in" result="p1" />
      <feFlood style={FLOOD} floodOpacity={0.5} result="c2" />
      <feComposite in="c2" in2="sRim" operator="in" result="p2" />
      <feMerge>
        <feMergeNode in="p1" />
        <feMergeNode in="p2" />
        <feMergeNode in="litP" />
      </feMerge>
    </filter>
  );
}

/** The projector's base: a ring on the ground as wide as the mass, an inner ring, and two faint beams rising. */
function ProjectorBase({ cx, reach, coreR }: { cx: number; reach: number; coreR: number }) {
  return (
    <g className={styles.projector}>
      <ellipse className={styles.projectorOuter} cx={cx} cy={GROUND_Y} rx={reach} ry={reach * 0.17} />
      <ellipse className={styles.projectorInner} cx={cx} cy={GROUND_Y} rx={reach * 0.55} ry={reach * 0.55 * 0.17} />
      {[-1, 1].map((s) => (
        <line
          key={s}
          className={styles.projectorBeam}
          x1={cx + s * reach * 0.92}
          y1={GROUND_Y}
          x2={cx + s * coreR * 0.7}
          y2={380}
        />
      ))}
    </g>
  );
}

/** Four ticks round a point, leaving its middle clear. */
const crosshair = (x: number, y: number) =>
  `M${x - 20} ${y} H${x - 6} M${x + 6} ${y} H${x + 20} M${x} ${y - 20} V${y - 6} M${x} ${y + 6} V${y + 20}`;

/** The tracked callout: a sight on the centre of mass, a leader out to MASS and its armour (+cover). */
function MassCallout({ target, at: [mx, my] }: { target: Target; at: [number, number] }) {
  const cover = (target.cover?.ap ?? 0) > 0 && isLocCoveredForBody(target.cover?.level ?? 'none', 'mass', AMORPHOUS);
  return (
    <g className={clsx(styles.gooCallout, cover && styles.covered)} data-callout="mass">
      <circle className={styles.gooSight} cx={mx} cy={my} r={11} />
      <path className={styles.gooSight} d={crosshair(mx, my)} />
      <polyline className={styles.gooLeader} points={`${mx + 12},${my - 8} 548,150 578,150`} />
      <text className={styles.gooName} x={585} y={118}>
        MASS
      </text>
      <text className={styles.gooNumeral} x={585} y={160}>
        {target.armour.mass ?? 0}
        <CoverPlus ap={cover ? target.cover.ap : null} />
      </text>
    </g>
  );
}

type Box = ReturnType<typeof gooBounds>;

/** The width under the mass and the height beside it, in metres off the sheet. */
function Dimensions({ box: { x0, x1, y0, y1 }, metres }: { box: Box; metres: { width: number; height: number } }) {
  const yb = y1 + 34;
  const xl = x0 - 34;
  return (
    <g className={styles.gooDimensions} data-testid="goo-dimensions">
      <path d={`M${x0} ${yb} H${x1} M${x0} ${yb - 8} V${yb + 8} M${x1} ${yb - 8} V${yb + 8}`} />
      <path d={`M${xl} ${y0} V${y1} M${xl - 8} ${y0} H${xl + 8} M${xl - 8} ${y1} H${xl + 8}`} />
      <path
        className={styles.gooExtension}
        d={`M${x0} ${y1} V${yb + 10} M${x1} ${y1} V${yb + 10} M${x0} ${y0} H${xl - 10} M${x0} ${y1} H${xl - 10}`}
      />
      <text x={(x0 + x1) / 2} y={yb + 22} textAnchor="middle">{`${metres.width.toFixed(1)} m`}</text>
      <text x={xl - 14} y={(y0 + y1) / 2} textAnchor="middle" transform={`rotate(-90 ${xl - 14} ${(y0 + y1) / 2})`}>
        {`${metres.height.toFixed(1)} m`}
      </text>
    </g>
  );
}

/** The words under the projector that a count cannot say (statusWords); the wounds are the panel's readout. */
function GooWounds({ state, left }: { state: PlateState; left: number }) {
  return (
    <StatusLine label={statusWords(state)} at={[left, 750]} testid="plate-wounds" labelClass={styles.gooWoundsLabel} />
  );
}

/** The moment the still form shows its scene at: the voice's pulse and the falling drops caught where they read. */
const STILL_T = 0.4;

/** The mass with motion off: settled at rest under its conditions (Dying and Dead at full), and its scene then. */
function stillScene(s: PlateState) {
  const c = gooConditionOf(s, s.dying ? 1 : 0, s.dead ? 1 : 0);
  const body = gooBody(0, STILL_T, c, true);
  return { drops: settledDrops(body), scene: gooScene(body, STILL_T, s, c) };
}

/** How fast Dead and Dying ease in: the mass settles over a second or two, it does not snap. */
const EASE = { dead: 1.5, dying: 0.8 };

/** What the mass carries from frame to frame: its own clock, the last frame's time, Dead and Dying eased. */
interface Live {
  idle: number;
  last: number;
  dead: number;
  dying: number;
}

/** One frame on: Dead and Dying eased toward the state, the mass's clock run at its pace (nearly stopped once dead). */
function step(L: Live, s: PlateState, t: number) {
  // Clamped: after a hidden tab or a paused clock the mass picks up where it was, it does not jump the whole gap.
  const dt = L.last < 0 ? 0 : Math.min(0.1, Math.max(0, t - L.last));
  L.last = t;
  L.dead = approach(L.dead, Number(s.dead), dt * EASE.dead);
  L.dying = approach(L.dying, Number(s.dying), dt * EASE.dying);
  const c = gooConditionOf(s, L.dying, L.dead);
  L.idle += dt * gooPace(c) * (1 - 0.97 * L.dead);
  return c;
}

/**
 * The goo's trait looks (plan design-gaps, P8): its traits worn round its mass, drawn in a figure's units inside a
 * group scaled down to the goo's (the study's 0.3), so a look sized for a figure fits the mass; what moves is written
 * by the goo's own clock.
 */
function useGooLooks(
  target: Target,
  sheet: Character | null,
  dead: boolean,
  u: string,
  hits: ReturnType<typeof usePlateHits>,
) {
  const d = useMemo(
    () => boxDraw(traitLooks(target, sheet, 'human'), FIGURE_BOX, guardOf(target), hits.play, dead),
    [target, sheet, dead, hits.play],
  );
  return { d, frame: traitFrame(d, u, hits.play, hits.elapsed) };
}

/** The goo's trait looks behind the mass, with their defs, in a figure's units scaled to the goo's. */
function GooTraitsBehind({ u, d, play }: { u: string; d: TraitDraw; play: HitPlay | null }) {
  return (
    <>
      <defs>
        <TraitDefs u={u} d={d} />
      </defs>
      <g transform={IN_FIGURE_UNITS}>
        <TraitUnder u={u} d={d} frame={GOO_FRAME_IN_FIGURE_UNITS} />
        <TraitBack u={u} d={d} play={play} />
      </g>
    </>
  );
}

/** The goo's trait looks before the mass. */
function GooTraitsBefore({ u, d, play }: { u: string; d: TraitDraw; play: HitPlay | null }) {
  return (
    <g transform={IN_FIGURE_UNITS}>
      <TraitFront u={u} d={d} play={play} />
      <TraitOver u={u} d={d} />
    </g>
  );
}

/** The goo's box and frame in a figure's units, and the transform that brings a figure's units down to the goo's. */
const FIGURE_BOX = GOO_BOUNDS.map((v) => v / GOO_FRAME.k) as unknown as FrameBox;
const IN_FIGURE_UNITS = `scale(${GOO_FRAME.k})`;
const GOO_FRAME_IN_FIGURE_UNITS = {
  x: VB.x / GOO_FRAME.k,
  y: VB.y / GOO_FRAME.k,
  w: VB.w / GOO_FRAME.k,
  h: VB.h / GOO_FRAME.k,
};

/**
 * The mass on the clock: each frame one step on (its clock accumulated at its pace - scaling time would teleport it
 * when the pace changes), then the drops placed and the scene written; when the clock stops, its still form again.
 */
function useGooLive(
  s: PlateState,
  still: ReturnType<typeof stillScene>,
  hits: ReturnType<typeof usePlateHits>,
  traits: TraitFrame,
) {
  const live = useRef<Live>({ idle: 0, last: -1, dead: Number(s.dead), dying: Number(s.dying) });
  // Its idle is its nature: it moves until it has settled dead, a still puddle - then the clock may stop.
  const [settled, setSettled] = useState(false);
  const writerOf = useSceneWriter();
  const onFrame = (t: number, root: SVGSVGElement) => {
    const c = step(live.current, s, t);
    if (settled !== (live.current.dead === 1)) setSettled(!settled);
    const struck = hits.play ? gooHitsOf(hits.play.burst.hits, hits.play.id, hits.elapsed(t)) : [];
    const body = struckGoo(gooBody(live.current.idle, t, c), struck, live.current.idle, t, live.current.dead);
    writeScene(writerOf(root), [
      ...massNodes(massDrops(body), [body.cx, body.cy, body.cr]),
      ...gooScene(body, t, s, c),
      ...sparkNodes(gooSparks(struck, body)),
      ...traits.at(t),
    ]);
  };
  /** The clock stopped: the mass back to its still form, and its own clock to pick up from there, not jump. */
  const onStop = (root: SVGSVGElement) => {
    live.current.last = -1;
    applyScene(root, [...massNodes(still.drops), ...still.scene, ...traits.still()]);
  };
  return { moving: !(s.dead && settled), onFrame, onStop };
}

/** Every layer of the mass's drops - the volume and each wall - as the scene the clock writes. */
const MASS_LAYERS = [{ key: 'vol', pull: 0, size: 1 }, ...WALLS.map((w, k) => ({ key: `w${k}`, ...w }))];
const massNodes = (drops: readonly Drop[], middle?: Drop) =>
  MASS_LAYERS.flatMap((l) => dropNodes(l.key, drops, l.pull, l.size, middle));
